import React, { useEffect, useState } from "react";
import "./style.sass";
import { LatexRenderer } from "../../components/latex-renderer";

const sanitizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  let trimmed = url.trim();
  trimmed = trimmed.replace(/(%22|%27|["',\\\/])+$/gi, "").trim();
  trimmed = trimmed.replace(/^(%22|%27|["'\\\/])+/gi, "").trim();
  if (
    !trimmed ||
    trimmed === "null" ||
    trimmed === "undefined" ||
    trimmed === "none" ||
    trimmed === "[]" ||
    trimmed === "{}"
  ) {
    return null;
  }

  // Route question_assets through https://rovianai.com/secure-api/ for valid SSL & HTTP 200
  if (trimmed.includes("question_assets")) {
    const assetPart = trimmed.substring(trimmed.indexOf("question_assets"));
    return `https://rovianai.com/secure-api/${assetPart}`;
  }

  if (trimmed.includes("visal1chatbotvm.southindia.cloudapp.azure.com")) {
    trimmed = trimmed.replace("https://", "http://");
    if (!trimmed.includes(":8001")) {
      trimmed = trimmed.replace(
        "visal1chatbotvm.southindia.cloudapp.azure.com",
        "visal1chatbotvm.southindia.cloudapp.azure.com:8001"
      );
    }
    trimmed = trimmed.replace(":8001/secure-api/", ":8001/");
  }

  return trimmed;
};

const isImgUrl = (str) => {
  if (typeof str !== "string") return false;
  const s = str.trim();
  return (
    s.startsWith("http://") ||
    s.startsWith("https://") ||
    s.startsWith("data:image/") ||
    s.startsWith("blob:") ||
    /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(s)
  );
};

const getQuestionImageUrls = (q) => {
  if (!q) return [];
  if (Array.isArray(q.image_urls) && q.image_urls.length > 0) {
    return q.image_urls.map(sanitizeImageUrl).filter(Boolean);
  }
  if (Array.isArray(q.imageUrls) && q.imageUrls.length > 0) {
    return q.imageUrls.map(sanitizeImageUrl).filter(Boolean);
  }
  if (Array.isArray(q.images) && q.images.length > 0) {
    return q.images.map(sanitizeImageUrl).filter(Boolean);
  }
  const single = q.image_url || q.imageUrl || q.image || q.diagram || q.question_image;
  if (single && typeof single === "string" && single.trim() !== "") {
    return [single.trim()];
  }
  return [];
};

const getOptionImage = (q, key, index) => {
  if (!q) return null;
  const optLetter = String(key).toUpperCase();
  const optLetterLower = String(key).toLowerCase();

  const optImages =
    q.option_images || q.optionImages || q.options_images || q.option_image;

  if (optImages) {
    if (Array.isArray(optImages)) {
      if (optImages[index]) return optImages[index];
    } else if (typeof optImages === "object") {
      const img =
        optImages[optLetter] ||
        optImages[optLetterLower] ||
        optImages[key] ||
        optImages[index];
      if (img && typeof img === "string" && img.trim() !== "") {
        return img.trim();
      }
    }
  }

  const val = q.options?.[key];
  if (typeof val === "string" && isImgUrl(val)) {
    return val.trim();
  }
  if (
    val &&
    typeof val === "object" &&
    (val.image || val.img || val.imageUrl || val.image_url)
  ) {
    return val.image || val.img || val.imageUrl || val.image_url;
  }

  return null;
};

const getOptionText = (rawValue) => {
  if (typeof rawValue === "string") {
    if (isImgUrl(rawValue)) return "";
    return rawValue.trim();
  }
  if (rawValue && typeof rawValue === "object") {
    return rawValue.text || rawValue.title || "";
  }
  return "";
};

export const AdminQuizPlay = ({
  currentQuestion,
  leaderboard,
}) => {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (!currentQuestion?.timer_seconds) return;

    setTimeLeft(currentQuestion.timer_seconds);

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [currentQuestion]);

  if (!currentQuestion) {
    return <h2>Waiting for question...</h2>;
  }

  const progress =
    ((currentQuestion.index + 1) /
      currentQuestion.total) *
    100;

  const questionImages = getQuestionImageUrls(currentQuestion);

  const optionsEntries = currentQuestion.options
    ? Array.isArray(currentQuestion.options)
      ? currentQuestion.options.map((val, idx) => [
          String.fromCharCode(65 + idx),
          val,
        ])
      : Object.entries(currentQuestion.options)
    : [];

  return (
    <section className="quiz-play">
      <section className="quiz-layout">
        {/* LEFT */}
        <section className="quiz-card">
          <section className="quiz-header">
            <span className="question-count">
              Q - {currentQuestion.index + 1}/
              {currentQuestion.total}
            </span>

            <span className="timer-quiz">
              00:{String(timeLeft).padStart(2, "0")}
            </span>
          </section>

          {/* Progress */}
          <section className="progress-bar">
            <section
              className="progress-fill"
              style={{
                width: `${
                  (timeLeft /
                    currentQuestion.timer_seconds) *
                  100
                }%`,
              }}
            />
          </section>

          {/* Question */}
          <h3 className="question">
            <LatexRenderer
              text={currentQuestion.question || currentQuestion.question_text || ""}
            />
          </h3>

          {/* Question Diagram Images */}
          {questionImages.length > 0 && (
            <section className="quiz-question-images">
              {questionImages.map((imgUrl, i) => (
                <img
                  key={i}
                  src={imgUrl}
                  alt={`Question Diagram ${i + 1}`}
                  className="quiz-diagram-img"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ))}
            </section>
          )}

          {/* Options */}
          <section className="options">
            {optionsEntries.map(([key, value], idx) => {
              const optImg = getOptionImage(currentQuestion, key, idx);
              const optText = getOptionText(value);
              const isCorrect =
                currentQuestion.showAnswer &&
                (currentQuestion.correctAnswer === key ||
                  String(currentQuestion.correctAnswer).toUpperCase() ===
                    String(key).toUpperCase());

              return (
                <section
                  key={key}
                  className={`option ${isCorrect ? "correct" : ""}`}
                >
                  <span className="radio"></span>

                  <div className="option-content-wrapper">
                    <div className="option-text-row">
                      <b className="option-key-badge">{key}.</b>
                      {optText && optText.trim() !== "" && (
                        <span className="option-text">
                          <LatexRenderer text={optText} />
                        </span>
                      )}
                    </div>

                    {optImg && (
                      <div className="option-img-container">
                        <img
                          src={optImg}
                          alt={`Option ${key}`}
                          className="option-diagram-img"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </div>
                    )}
                  </div>
                </section>
              );
            })}
          </section>
        </section>
      </section>

      {/* Bottom */}
      <section className="bottom-bar">
        <section className="bottom-bar-section">
          <section className="progress-section">
            <span className="progress-label">
              Quiz Progress
              <b>
                {" "}
                {currentQuestion.index + 1}/
                {currentQuestion.total}
              </b>
            </span>

            <section className="progress-track">
              <section
                className="progress-bar-fill"
                style={{
                  width: `${progress}%`,
                }}
              />
            </section>
          </section>
        </section>
      </section>
    </section>
  );
};